/**
 * MOTE-123 — recurrence rule editor. 1:1 port of the legacy
 * `3mote-fe/src/components/Recurring/Recurring.jsx`:
 *   - Repeats every {N} {Day/Week/Month/Year}
 *   - Weekly: 7-day chip picker
 *   - Monthly: day-of-month (1–30 + Last) OR day-of-week (Nth weekday)
 *   - Ends: Never | After {N} times | On {date}
 *   - Completion behavior: on_completion vs on_date (two-card radio)
 *   - Human-readable summary line
 *
 * Styling is Tailwind v4 + the new DS primitives (`<Dropdown>`,
 * `<FormField>`) — no styled-components. The shape stays one-rule-per
 * deliverable; the parent owns the controlled state and submission.
 */

import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import type { RecurrenceOrdinal, RecurrenceWeekday } from '@/api/types-bridge'
import { Dropdown } from '@/components/Dropdown'
import {
  DATE_OF_MONTH_OPTIONS,
  END_TYPE_OPTIONS,
  FREQUENCY_OPTIONS,
  ORDINAL_OPTIONS,
  type RecurrenceFormState,
  WEEKDAY_OPTIONS,
} from '@/features/board/recurrence-utils'
import { formatList } from '@/i18n/formatters'

interface RecurrenceEditorProps {
  value: RecurrenceFormState
  onChange: (next: RecurrenceFormState) => void
  /**
   * Used by the human-readable summary line ("this task repeats every …"
   * vs "this event repeats every …"). Defaults to `task`.
   */
  itemType?: 'task' | 'event'
  disabled?: boolean
  error?: string | null
  /**
   * `'rail'` lays the editor out for the 312px card-detail rail: stacked
   * full-width controls, a 7-column weekday grid, and segmented radio
   * groups for Ends and the monthly mode. `'default'` is the original form.
   */
  layout?: 'default' | 'rail'
}

/**
 * Segmented single-select built as a real `role="radiogroup"` with a roving
 * tabindex: only the checked segment is tabbable, arrows / Home / End move
 * (and select) like native radios.
 */
function Segmented<T extends string>({
  label,
  value,
  onChange,
  options,
  disabled,
}: {
  label: string
  value: T
  onChange: (next: T) => void
  options: ReadonlyArray<{ value: T; label: string }>
  disabled: boolean
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([])
  const activeIndex = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  )
  const move = (to: number) => {
    const next = options[(to + options.length) % options.length]
    if (!next) return
    onChange(next.value)
    refs.current[(to + options.length) % options.length]?.focus()
  }
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault()
        move(activeIndex + 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault()
        move(activeIndex - 1)
        break
      case 'Home':
        e.preventDefault()
        move(0)
        break
      case 'End':
        e.preventDefault()
        move(options.length - 1)
        break
    }
  }
  return (
    <div
      role="radiogroup"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="flex gap-0.5 rounded-md border border-border bg-bg-muted p-0.5"
    >
      {options.map((o, i) => {
        const selected = o.value === value
        return (
          <button
            key={o.value}
            ref={(el) => {
              refs.current[i] = el
            }}
            type="button"
            // biome-ignore lint/a11y/useSemanticElements: a segmented control is a styled button group; a native radio input cannot be laid out as a segment with roving tabindex here.
            role="radio"
            aria-checked={selected}
            tabIndex={i === activeIndex ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            className={[
              'flex-1 rounded px-2 py-1.5 text-xs transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              'disabled:cursor-not-allowed disabled:opacity-60',
              selected
                ? 'bg-bg-elevated font-semibold text-fg shadow-sm ring-1 ring-fg-muted'
                : 'font-medium text-fg-muted hover:text-fg',
            ].join(' ')}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

const RAIL_END_ORDER: ReadonlyArray<'never' | 'after' | 'on'> = ['never', 'after', 'on']

export function RecurrenceEditor({
  value,
  onChange,
  itemType = 'task',
  disabled = false,
  error,
  layout = 'default',
}: RecurrenceEditorProps) {
  const rail = layout === 'rail'
  const { t, i18n } = useTranslation('board')

  // i18next's `t` carries many overloads from the strict-key
  // CustomTypeOptions declaration merge. Two patterns here both need
  // the lax overload: (1) `t(o.labelKey)` callsites where the key is
  // a runtime string from a Recurrence*_OPTIONS row, not a known
  // catalog key; (2) the `buildSummary(... t ...)` call below, which
  // would otherwise trigger a TS overload-resolution crash inside the
  // useMemo body (`Debug Failure. No error for last overload
  // signature`). Single cast — same workaround as
  // `chat/components/MessageReactions.tsx`'s `t: any` prop.
  // biome-ignore lint/suspicious/noExplicitAny: collapse i18next strict-key overloads in one place
  const tAny = t as any

  const set = <K extends keyof RecurrenceFormState>(key: K, next: RecurrenceFormState[K]) => {
    onChange({ ...value, [key]: next })
  }

  const toggleWeekday = (day: RecurrenceWeekday) => {
    const has = value.daysOfWeek.includes(day)
    onChange({
      ...value,
      daysOfWeek: has ? value.daysOfWeek.filter((d) => d !== day) : [...value.daysOfWeek, day],
    })
  }

  const switchMonthlyMode = (mode: RecurrenceFormState['monthlyMode']) => {
    if (mode === 'day_of_month') {
      onChange({
        ...value,
        monthlyMode: 'day_of_month',
        dateOfMonth: value.dateOfMonth ?? 1,
      })
    } else {
      onChange({
        ...value,
        monthlyMode: 'day_of_week',
        monthlyOrdinal: value.monthlyOrdinal ?? 'first',
        monthlyWeekday: value.monthlyWeekday ?? 'monday',
      })
    }
  }

  const summary = useMemo(
    () => buildSummary(value, itemType, tAny, i18n.language),
    [value, itemType, tAny, i18n.language],
  )

  const isInvalidInterval = !Number.isFinite(value.interval) || value.interval < 1

  return (
    <div
      className={`flex flex-col gap-4 rounded-md border border-border bg-bg-muted ${rail ? 'p-3' : 'p-4'}`}
    >
      {/* Repeats every */}
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-fg">
          {t('recurrence.label.repeatsEvery')}
          <span aria-hidden="true" className="ms-0.5 text-danger">
            *
          </span>
        </span>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            value={Number.isFinite(value.interval) ? value.interval : ''}
            onChange={(e) => set('interval', Number(e.target.value))}
            disabled={disabled}
            aria-label={t('recurrence.label.interval')}
            className={`w-24 rounded-md border ${
              isInvalidInterval ? 'border-danger' : 'border-border-strong'
            } bg-bg-elevated px-3 py-2 text-sm text-fg shadow-sm focus:border-fg focus:outline-none focus:ring-1 focus:ring-fg disabled:cursor-not-allowed disabled:bg-bg-muted`}
          />
          <div className={rail ? 'min-w-0 flex-1' : 'min-w-[10rem]'}>
            <Dropdown
              value={value.frequency}
              onChange={(v) => set('frequency', v as RecurrenceFormState['frequency'])}
              options={FREQUENCY_OPTIONS.map((o) => ({
                value: o.value,
                label: tAny(o.labelKey),
              }))}
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {/* Weekly: day chip picker */}
      {value.frequency === 'weekly' && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-fg">
            {t('recurrence.label.repeatOn')}
            <span aria-hidden="true" className="ms-0.5 text-danger">
              *
            </span>
          </span>
          <div className={rail ? 'grid grid-cols-7 gap-1' : 'flex flex-wrap gap-2'}>
            {WEEKDAY_OPTIONS.map((day) => {
              const selected = value.daysOfWeek.includes(day.value)
              return (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleWeekday(day.value)}
                  disabled={disabled}
                  aria-pressed={selected}
                  className={[
                    rail
                      ? 'rounded-md border px-0 py-1.5 text-xs font-medium transition-colors'
                      : 'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                    'disabled:cursor-not-allowed disabled:opacity-60',
                    selected
                      ? 'border-accent bg-accent text-bg-elevated'
                      : 'border-border-strong bg-bg-elevated text-fg-muted hover:text-fg',
                  ].join(' ')}
                >
                  {tAny(day.labelKey).slice(0, 3)}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Monthly options */}
      {value.frequency === 'monthly' && (
        <div className="flex flex-col gap-2">
          <span className="text-sm font-medium text-fg">
            {t('recurrence.label.repeatOn')}
            <span aria-hidden="true" className="ms-0.5 text-danger">
              *
            </span>
          </span>
          <div
            className={
              rail
                ? value.monthlyMode === 'day_of_week'
                  ? 'grid grid-cols-[112px_1fr] items-center gap-2'
                  : 'flex flex-col gap-2'
                : 'flex flex-wrap items-center gap-2'
            }
          >
            {rail ? (
              <div className="col-span-2">
                <Segmented
                  label={t('recurrence.label.repeatOn')}
                  value={value.monthlyMode}
                  onChange={switchMonthlyMode}
                  options={[
                    { value: 'day_of_month', label: t('recurrence.monthlyMode.dayOfMonth') },
                    { value: 'day_of_week', label: t('recurrence.monthlyMode.dayOfWeek') },
                  ]}
                  disabled={disabled}
                />
              </div>
            ) : (
              <div className="min-w-[10rem]">
                <Dropdown
                  value={value.monthlyMode}
                  onChange={(v) => switchMonthlyMode(v as RecurrenceFormState['monthlyMode'])}
                  options={[
                    { value: 'day_of_month', label: t('recurrence.monthlyMode.dayOfMonth') },
                    { value: 'day_of_week', label: t('recurrence.monthlyMode.dayOfWeek') },
                  ]}
                  disabled={disabled}
                />
              </div>
            )}

            {value.monthlyMode === 'day_of_month' ? (
              <div className={rail ? 'min-w-0' : 'min-w-[7rem]'}>
                <Dropdown
                  value={String(value.dateOfMonth ?? 1)}
                  onChange={(v) => set('dateOfMonth', Number(v))}
                  options={DATE_OF_MONTH_OPTIONS.map((o) => ({
                    value: String(o.value),
                    label: tAny(o.labelKey),
                  }))}
                  disabled={disabled}
                />
              </div>
            ) : (
              <>
                <div className={rail ? 'min-w-0' : 'min-w-[7rem]'}>
                  <Dropdown
                    value={value.monthlyOrdinal}
                    onChange={(v) => set('monthlyOrdinal', v as RecurrenceOrdinal)}
                    options={ORDINAL_OPTIONS.map((o) => ({
                      value: o.value,
                      label: tAny(o.labelKey),
                    }))}
                    disabled={disabled}
                  />
                </div>
                <div className={rail ? 'min-w-0' : 'min-w-[10rem]'}>
                  <Dropdown
                    value={value.monthlyWeekday}
                    onChange={(v) => set('monthlyWeekday', v as RecurrenceWeekday)}
                    options={WEEKDAY_OPTIONS.map((o) => ({
                      value: o.value,
                      label: tAny(o.labelKey),
                    }))}
                    disabled={disabled}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Ends */}
      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-fg">
          {t('recurrence.label.ends')}
          <span aria-hidden="true" className="ms-0.5 text-danger">
            *
          </span>
        </span>
        <div className={rail ? 'flex flex-col gap-2' : 'flex flex-wrap items-center gap-2'}>
          {rail ? (
            <Segmented
              label={t('recurrence.label.ends')}
              value={value.endType}
              onChange={(v) => set('endType', v)}
              options={RAIL_END_ORDER.map((v) => ({
                value: v,
                label: tAny(`recurrence.endSegment.${v}`),
              }))}
              disabled={disabled}
            />
          ) : (
            <div className="min-w-[7rem]">
              <Dropdown
                value={value.endType}
                onChange={(v) => set('endType', v as RecurrenceFormState['endType'])}
                options={END_TYPE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: tAny(o.labelKey),
                }))}
                disabled={disabled}
              />
            </div>
          )}

          {value.endType === 'after' && (
            <div className={rail ? 'w-full' : 'w-36'}>
              <input
                type="number"
                min={1}
                value={value.occurrences ?? ''}
                onChange={(e) =>
                  set('occurrences', e.target.value === '' ? null : Number(e.target.value))
                }
                disabled={disabled}
                aria-label={t('recurrence.label.occurrences')}
                placeholder={t('recurrence.placeholder.repetitions')}
                className="w-full rounded-md border border-border-strong bg-bg-elevated px-3 py-2 text-sm text-fg shadow-sm focus:border-fg focus:outline-none focus:ring-1 focus:ring-fg disabled:cursor-not-allowed disabled:bg-bg-muted"
              />
            </div>
          )}

          {value.endType === 'on' && (
            <div className={rail ? 'w-full' : 'w-44'}>
              <input
                type="date"
                value={value.endDate ?? ''}
                onChange={(e) => set('endDate', e.target.value || null)}
                disabled={disabled}
                aria-label={t('recurrence.label.endDate')}
                min={todayPlusOneIso()}
                className="w-full rounded-md border border-border-strong bg-bg-elevated px-3 py-2 text-sm text-fg shadow-sm focus:border-fg focus:outline-none focus:ring-1 focus:ring-fg disabled:cursor-not-allowed disabled:bg-bg-muted"
              />
            </div>
          )}
        </div>
      </div>

      {/* Completion behavior — two-card radio. */}
      <div className="flex flex-col gap-2 border-t border-border pt-3">
        <span className="text-sm font-medium text-fg">
          {t('recurrence.label.completionBehavior')}
          <span aria-hidden="true" className="ms-0.5 text-danger">
            *
          </span>
        </span>
        <p className="text-xs text-fg-muted">
          {t('recurrence.label.completionHelp', {
            itemType: t(`recurrence.itemType.${itemType}`),
          })}
        </p>

        <CompletionOption
          selected={value.triggerType === 'on_completion'}
          onSelect={() => set('triggerType', 'on_completion')}
          disabled={disabled}
          title={t('recurrence.completion.onCompletionTitle')}
          subtitle={t('recurrence.completion.onCompletionHelp', {
            itemType: t(`recurrence.itemType.${itemType}`),
          })}
        />
        <CompletionOption
          selected={value.triggerType === 'on_date'}
          onSelect={() => set('triggerType', 'on_date')}
          disabled={disabled}
          title={t('recurrence.completion.onDateTitle')}
          subtitle={t('recurrence.completion.onDateHelp', {
            itemType: t(`recurrence.itemType.${itemType}`),
          })}
        />
      </div>

      {/* Human-readable summary line */}
      <p className="text-xs italic text-fg-muted">{summary}</p>

      {error && (
        <p role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  )
}

interface CompletionOptionProps {
  selected: boolean
  onSelect: () => void
  disabled: boolean
  title: string
  subtitle: string
}

function CompletionOption({
  selected,
  onSelect,
  disabled,
  title,
  subtitle,
}: CompletionOptionProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      className={[
        'flex items-start gap-3 rounded-md border px-3 py-2 text-start transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        selected
          ? 'border-accent bg-bg-elevated'
          : 'border-border bg-bg-elevated hover:border-border-strong',
      ].join(' ')}
    >
      <span
        aria-hidden="true"
        className={[
          'mt-0.5 inline-flex h-4 w-4 flex-none items-center justify-center rounded-full border-2 bg-bg-elevated',
          selected ? 'border-accent' : 'border-border-strong',
        ].join(' ')}
      >
        {selected && <span className="block h-2 w-2 rounded-full bg-accent" />}
      </span>
      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-medium text-fg">{title}</span>
        <span className="text-xs text-fg-muted">{subtitle}</span>
      </span>
    </button>
  )
}

/**
 * Styled checkbox used by the "Make {Item} Recurring → Recurring" toggle
 * on both create + detail surfaces. Filled accent square with white check
 * when checked, empty rounded square when not — matches the visual in the
 * spec image (image #1). Native `<input type="checkbox">` is visually
 * hidden but stays in the tab order for keyboard / screen-reader users.
 */
export function RecurringCheckbox({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
}) {
  return (
    <span className="relative inline-flex h-5 w-5 flex-none items-center justify-center">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="peer absolute inset-0 h-full w-full cursor-pointer appearance-none rounded-md border border-border-strong bg-bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:cursor-not-allowed disabled:opacity-60 checked:border-accent checked:bg-accent"
      />
      {checked && (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="pointer-events-none relative text-bg-elevated"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </span>
  )
}

/** `yyyy-MM-dd` for "tomorrow" — the legacy `minDate` floor on the date picker. */
function todayPlusOneIso(): string {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/* -------------------------- Summary line builder -------------------------- */

/** The human-readable rule sentence, shared with the rail's saved-state card. */
export function recurrenceSummary(
  state: RecurrenceFormState,
  itemType: 'task' | 'event',
  // biome-ignore lint/suspicious/noExplicitAny: collapse i18next strict-key overloads in one place
  t: any,
  locale: string,
): string {
  return buildSummary(state, itemType, t, locale)
}

function buildSummary(
  state: RecurrenceFormState,
  itemType: 'task' | 'event',
  t: (key: string, opts?: Record<string, unknown>) => string,
  locale: string,
): string {
  const item = t(`recurrence.itemType.${itemType}`)
  const intervalIsOne = state.interval === 1
  // Singular / plural via ICU on the catalog side.
  const frequencyName = t(`recurrence.frequencyNoun.${state.frequency}`, {
    count: intervalIsOne ? 1 : state.interval,
  })

  let line = t('recurrence.summary.prefix', {
    item,
    interval: state.interval,
    frequency: frequencyName,
  })

  if (state.frequency === 'weekly' && state.daysOfWeek.length > 0) {
    // Days are stored in click order; read them Monday → Sunday.
    const dayNames = formatList(
      WEEKDAY_OPTIONS.filter((o) => state.daysOfWeek.includes(o.value))
        .map((o) => t(o.labelKey))
        .filter(Boolean),
      locale,
    )
    if (dayNames) line += ` ${t('recurrence.summary.onDays', { days: dayNames })}`
  }

  if (state.frequency === 'monthly') {
    if (state.monthlyMode === 'day_of_month' && state.dateOfMonth != null) {
      const label =
        DATE_OF_MONTH_OPTIONS.find((o) => o.value === state.dateOfMonth)?.labelKey ?? null
      if (label) {
        line += ` ${t('recurrence.summary.onDateOfMonth', { date: t(label) })}`
      }
    } else if (state.monthlyMode === 'day_of_week') {
      line += ` ${t('recurrence.summary.onOrdinalWeekday', {
        ordinal: t(`recurrence.ordinal.${state.monthlyOrdinal}`),
        weekday: t(`recurrence.weekday.${state.monthlyWeekday}`),
      })}`
    }
  }

  if (state.endType === 'after' && state.occurrences != null && state.occurrences > 0) {
    line += `, ${t('recurrence.summary.afterTimes', { count: state.occurrences })}`
  } else if (state.endType === 'on' && state.endDate) {
    line += `, ${t('recurrence.summary.untilDate', { date: formatHumanDate(state.endDate, locale) })}`
  } else if (state.endType === 'never') {
    line += `, ${t('recurrence.summary.noEndDate')}`
  }

  return line
}

function formatHumanDate(iso: string, locale: string): string {
  const d = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}
